
// consent.service.ts
import { Injectable } from '@angular/core';
import { setRootCookie, getCookie, deleteRootCookie } from '../utils/cookie-utils';

export type ConsentVal = 'allow' | 'deny';
export interface ComplianzConsent {
  functional: 'allow';
  preferences: ConsentVal | null;
  statistics:  ConsentVal | null;
  marketing:   ConsentVal | null;
  policyId?:   string | null;
  savedCategories?: string[];
}

@Injectable({ providedIn: 'root' })
export class ConsentService {

  read(): ComplianzConsent {
    const savedCategoriesCookie = getCookie('cmplz_saved_categories');
    const statisticsCookie = getCookie('cmplz_statistics');
    let savedCategories: string[] = ['functional'];
    
    if (savedCategoriesCookie) {
      try {
        const parsed = JSON.parse(savedCategoriesCookie);
        if (Array.isArray(parsed)) {
          savedCategories = parsed;
        }
      } catch (e) {
        // Se falhar, mantém o array padrão
      }
    }
    
    const consent = {
      functional: 'allow',
      preferences: getCookie('cmplz_preferences'),
      statistics: statisticsCookie,
      marketing:   getCookie('cmplz_marketing'),
      policyId:    getCookie('cmplz_policy_id'),
      savedCategories
    } as ComplianzConsent;
    
    console.log('🔍 CONSENT READ DEBUG - Final consent object:', consent);
    return consent;
  }

  hasAnyChoice(): boolean {
    // Verificar todos os cookies relevantes da Complianz
    const savedCategoriesCookie = getCookie('cmplz_saved_categories');
    const functionalCookie = getCookie('cmplz_functional');
    const preferencesCookie = getCookie('cmplz_preferences');
    const statisticsCookie = getCookie('cmplz_statistics');
    const marketingCookie = getCookie('cmplz_marketing');
    const policyIdCookie = getCookie('cmplz_policy_id');
    
    console.log('🔍 COOKIE DEBUG - cmplz_saved_categories:', savedCategoriesCookie);
    console.log('🔍 COOKIE DEBUG - cmplz_functional:', functionalCookie);
    console.log('🔍 COOKIE DEBUG - cmplz_preferences:', preferencesCookie);
    console.log('🔍 COOKIE DEBUG - cmplz_statistics:', statisticsCookie);
    console.log('🔍 COOKIE DEBUG - cmplz_marketing:', marketingCookie);
    console.log('🔍 COOKIE DEBUG - cmplz_policy_id:', policyIdCookie);
    console.log('🔍 COOKIE DEBUG - all cookies:', document.cookie);
    
    // Se existir o cookie cmplz_saved_categories e for válido, considere que há consentimento
    if (savedCategoriesCookie) {
      try {
        const savedCategories = JSON.parse(savedCategoriesCookie);
        const hasElements = Array.isArray(savedCategories) && savedCategories.length > 0;
        console.log('🔍 COOKIE DEBUG - parsed categories:', savedCategories, 'hasElements:', hasElements);
        if (hasElements) {
          return true;
        }
      } catch (e) {
        console.log('❌ COOKIE DEBUG - parse error in saved_categories:', e);
      }
    }
    
    // Verificar se existem cookies individuais da Complianz (indica consentimento prévio)
    const hasFunctionalCookie = functionalCookie === 'allow';
    const hasPreferencesConsent = preferencesCookie === 'allow' || preferencesCookie === 'deny';
    const hasStatisticsConsent = statisticsCookie === 'allow' || statisticsCookie === 'deny';
    const hasMarketingConsent = marketingCookie === 'allow' || marketingCookie === 'deny';
    
    console.log('🔍 COOKIE DEBUG - hasFunctionalCookie:', hasFunctionalCookie);
    console.log('🔍 COOKIE DEBUG - hasPreferencesConsent:', hasPreferencesConsent);
    console.log('🔍 COOKIE DEBUG - hasStatisticsConsent:', hasStatisticsConsent);
    console.log('🔍 COOKIE DEBUG - hasMarketingConsent:', hasMarketingConsent);
    
    // Se existir qualquer cookie de consentimento individual, considera que há escolha
    const hasAnyIndividualConsent = hasFunctionalCookie || 
                                   hasPreferencesConsent || 
                                   hasStatisticsConsent || 
                                   hasMarketingConsent;
    
    // Também verificar policy_id como fallback
    const hasPolicyId = !!policyIdCookie;
    
    const hasChoice = hasAnyIndividualConsent || hasPolicyId;
    console.log('🔍 COOKIE DEBUG - hasAnyIndividualConsent:', hasAnyIndividualConsent);
    console.log('🔍 COOKIE DEBUG - hasPolicyId:', hasPolicyId);
    console.log('🔍 COOKIE DEBUG - final hasChoice:', hasChoice);
    
    return hasChoice;
  }

  /** Grava o consentimento "à la Complianz" no domínio raiz */
  save(opts: {
    preferences?: ConsentVal | null;
    statistics?:  ConsentVal | null;
    marketing?:   ConsentVal | null;
    policyId?:    string | null;
  }) {
    // Sempre cria/atualiza o cookie functional como 'allow'
    setRootCookie('cmplz_functional', 'allow');
    
    // Cria/atualiza os cookies de consentimento (se fornecidos)
    if (opts.preferences !== undefined && opts.preferences !== null) {
      setRootCookie('cmplz_preferences', opts.preferences);
    }
    if (opts.statistics !== undefined && opts.statistics !== null) {
      setRootCookie('cmplz_statistics', opts.statistics);
    }
    if (opts.marketing !== undefined && opts.marketing !== null) {
      setRootCookie('cmplz_marketing', opts.marketing);
    }
    if (opts.policyId !== undefined && opts.policyId !== null) {
      setRootCookie('cmplz_policy_id', opts.policyId);
    }
    
    // Cria o cookie cmplz_saved_categories como array JSON
    const savedCategories = ['functional']; // sempre inclui functional
    
    if (opts.preferences === 'allow') savedCategories.push('preferences');
    if (opts.statistics === 'allow') savedCategories.push('statistics');
    if (opts.marketing === 'allow') savedCategories.push('marketing');
    
    setRootCookie('cmplz_saved_categories', JSON.stringify(savedCategories));
  }

  /** Reset total (para re‑apresentar banner) */
  resetAll() {
    ['cmplz_functional','cmplz_preferences','cmplz_statistics','cmplz_marketing','cmplz_policy_id','cmplz_saved_categories']
      .forEach(deleteRootCookie);
  }
}
